// src/pages/super-admin/CompanyDetailPage.js - 기업 상세 관리

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChartLine, faPlus, faRotateLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  createCompanyTokenTopup,
  deleteCompany,
  getCompanyDetail,
  getCompanyTokenBalance,
  resetCompanyTokenMetering,
  updateCompany,
} from '../../services/api';
import CompanyPersonaSchemaPanel from '../../components/super-admin/CompanyPersonaSchemaPanel';
import './EnterpriseListPage.scss';
import './CompanyUsagePage.scss';

const STATUS_LABELS = {
  active: '활성',
  inactive: '비활성',
  deleted: '삭제됨',
};

const PRESET_LABELS = {
  telecom: '통신 (Telecom)',
  finance: '금융/투자 (Finance)',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTokenCount = (value) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('ko-KR');
};

const getMemberLlmUsage = (member) => {
  const llmUsage = member.llm_usage || {};
  const legacyUsage = member.usage || {};
  return {
    weightedTokens: llmUsage.billable_weighted_tokens,
    rawTokens: llmUsage.total_tokens ?? legacyUsage.total_tokens,
    lastUsedAt: llmUsage.last_used_at || legacyUsage.last_used_at,
  };
};

function CompanyDetailPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [personaPresets, setPersonaPresets] = useState([]);
  const [statusDraft, setStatusDraft] = useState('active');
  const [presetDraft, setPresetDraft] = useState('telecom');
  const [loading, setLoading] = useState(true);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [tokenBalanceLoading, setTokenBalanceLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [error, setError] = useState(null);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupTokens, setTopupTokens] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupSaving, setTopupSaving] = useState(false);
  const [topupError, setTopupError] = useState(null);
  const [resetSaving, setResetSaving] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const isDeleted = company?.status === 'deleted';

  const fetchCompany = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getCompanyDetail(companyId);
      setCompany(data.company || null);
      setMembers(data.members || []);
      setPersonaPresets(data.persona_presets || []);
      setStatusDraft(data.company?.status || 'active');
      setPresetDraft(
        data.company?.personaTemplateId
          || data.company?.persona_template_id
          || data.company?.personaPresetId
          || data.company?.persona_preset_id
          || 'telecom'
      );
    } catch (err) {
      console.error('회사 상세 조회 실패:', err);
      setError(err.message || '회사 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const fetchTokenBalance = useCallback(async () => {
    if (!companyId) return;
    try {
      setTokenBalanceLoading(true);
      const data = await getCompanyTokenBalance(companyId);
      setTokenBalance(data);
    } catch (err) {
      console.error('기업 토큰 잔액 조회 실패:', err);
      setTokenBalance(null);
    } finally {
      setTokenBalanceLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchCompany();
    fetchTokenBalance();
  }, [fetchCompany, fetchTokenBalance]);

  const grantedTokens = Number(tokenBalance?.granted_weighted_tokens || 0);
  const usedTokens = Number(tokenBalance?.used_weighted_tokens || 0);
  const remainingTokens = Number(tokenBalance?.remaining_weighted_tokens || 0);
  const tokenPercent = grantedTokens > 0 ? Math.min(Math.round((usedTokens / grantedTokens) * 100), 100) : 0;

  const handleStatusSave = async () => {
    if (!company || statusDraft === company.status) return;
    try {
      setSavingStatus(true);
      await updateCompany(company.id, { status: statusDraft });
      await fetchCompany();
    } catch (err) {
      console.error('회사 상태 변경 실패:', err);
      alert(err.message || '회사 상태 변경에 실패했습니다.');
    } finally {
      setSavingStatus(false);
    }
  };

  const handlePresetSave = async () => {
    const currentPreset =
      company?.personaTemplateId
      || company?.persona_template_id
      || company?.personaPresetId
      || company?.persona_preset_id
      || 'telecom';
    if (!company || presetDraft === currentPreset) return;
    try {
      setSavingPreset(true);
      await updateCompany(company.id, { personaPresetId: presetDraft });
      await fetchCompany();
    } catch (err) {
      console.error('Persona 프리셋 변경 실패:', err);
      alert(err.message || 'Persona 프리셋 변경에 실패했습니다.');
    } finally {
      setSavingPreset(false);
    }
  };

  const presetOptions = personaPresets.length > 0
    ? personaPresets
    : Object.entries(PRESET_LABELS).map(([id, label]) => ({ id, label }));

  const handleTokenReset = async () => {
    const confirmed = window.confirm(
      '이 기업의 토큰 사용 기록과 LLM 사용 집계를 모두 초기화하고, 기본 100,000 토큰을 다시 지급합니다.\n\n계속하시겠습니까?'
    );
    if (!confirmed) return;

    try {
      setResetSaving(true);
      await resetCompanyTokenMetering(companyId);
      await Promise.all([fetchTokenBalance(), fetchCompany()]);
    } catch (err) {
      console.error('기업 토큰 기록 초기화 실패:', err);
      alert(err.message || '토큰 기록 초기화에 실패했습니다.');
    } finally {
      setResetSaving(false);
    }
  };

  const handleTopupSubmit = async (event) => {
    event.preventDefault();
    const weightedTokens = Number(topupTokens);
    if (!Number.isInteger(weightedTokens) || weightedTokens < 1) {
      setTopupError('충전 토큰은 1 이상의 정수로 입력해주세요.');
      return;
    }

    try {
      setTopupSaving(true);
      setTopupError(null);
      await createCompanyTokenTopup(companyId, {
        weighted_tokens: weightedTokens,
        note: topupNote.trim() || undefined,
      });
      setShowTopupModal(false);
      setTopupTokens('');
      setTopupNote('');
      await fetchTokenBalance();
    } catch (err) {
      console.error('기업 토큰 충전 실패:', err);
      setTopupError(err.message || '토큰 충전에 실패했습니다.');
    } finally {
      setTopupSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!company || isDeleted) return;
    const confirmed = window.confirm(
      `"${company.name}" 기업을 삭제 처리하시겠습니까?\n\n데이터는 보존되지만 목록에서 숨겨지며, 소속 계정의 기업 연결은 유지됩니다.`
    );
    if (!confirmed) return;

    try {
      setDeleteSaving(true);
      await deleteCompany(companyId);
      navigate('/super-admin/companies');
    } catch (err) {
      console.error('기업 삭제 실패:', err);
      alert(err.message || '기업 삭제에 실패했습니다.');
    } finally {
      setDeleteSaving(false);
    }
  };

  return (
    <div className="enterprise-list-page">
      <div className="sa-page-header">
        <div className="sa-page-header-left">
          <button
            className="sa-btn sa-btn-secondary sa-btn-sm"
            onClick={() => navigate('/super-admin/companies')}
            style={{ marginRight: 12 }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1 className="sa-page-title">{company?.name || `기업 #${companyId}`}</h1>
            <p className="sa-page-subtitle">
              기업 상세 관리
              {isDeleted && (
                <span className="sa-badge sa-badge-inactive" style={{ marginLeft: 8 }}>삭제됨</span>
              )}
            </p>
          </div>
        </div>
        {!isDeleted && (
          <button
            type="button"
            className="sa-btn sa-btn-secondary sa-btn-sm"
            onClick={handleDelete}
            disabled={deleteSaving}
            style={{ color: '#b91c1c', borderColor: '#fecaca' }}
          >
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
            {deleteSaving ? '삭제 중...' : '기업 삭제'}
          </button>
        )}
      </div>

      {loading && <div className="sa-loading">불러오는 중...</div>}

      {error && (
        <div className="sa-empty">
          <div className="sa-empty-icon">⚠️</div>
          <div className="sa-empty-text">{error}</div>
          <button className="sa-btn sa-btn-secondary" onClick={fetchCompany}>다시 시도</button>
        </div>
      )}

      {!loading && !error && company && (
        <>
          <div className="usage-stats-grid" style={{ marginBottom: 16 }}>
            <div className="sa-stat-card">
              <div className="sa-stat-label">서비스 상태</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                <select
                  className="sa-filter-select"
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  disabled={savingStatus || isDeleted}
                >
                  <option value="active">활성</option>
                  <option value="inactive">비활성</option>
                </select>
                <button
                  className="sa-btn sa-btn-secondary sa-btn-sm"
                  onClick={handleStatusSave}
                  disabled={savingStatus || isDeleted || statusDraft === company.status}
                >
                  {savingStatus ? '저장 중...' : '저장'}
                </button>
              </div>
              <div style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>
                현재 상태: {STATUS_LABELS[company.status] || company.status}
              </div>
            </div>

            <div className="sa-stat-card">
              <div className="sa-stat-label">Persona 프리셋</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                <select
                  className="sa-filter-select"
                  value={presetDraft}
                  onChange={(e) => setPresetDraft(e.target.value)}
                  disabled={savingPreset || isDeleted}
                >
                  {presetOptions.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {PRESET_LABELS[preset.id] || preset.label || preset.id}
                    </option>
                  ))}
                </select>
                <button
                  className="sa-btn sa-btn-secondary sa-btn-sm"
                  onClick={handlePresetSave}
                  disabled={
                    savingPreset
                    || isDeleted
                    || presetDraft === (
                      company.personaTemplateId
                      || company.persona_template_id
                      || company.personaPresetId
                      || company.persona_preset_id
                      || 'telecom'
                    )
                  }
                >
                  {savingPreset ? '저장 중...' : '저장'}
                </button>
              </div>
              <div style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>
                이 팀의 persona 생성·인터뷰 pack에 사용할 도메인 프리셋입니다.
              </div>
            </div>

            <div className="sa-stat-card token-balance-card">
              <div className="sa-stat-label">토큰 잔액</div>
              <div className="sa-stat-value accent">
                {tokenBalanceLoading ? '...' : formatTokenCount(remainingTokens)}
              </div>
              <div className="token-progress">
                <div className="token-progress-fill" style={{ width: `${tokenPercent}%` }} />
              </div>
              <div className="token-card-meta">
                <span>지급 {formatTokenCount(grantedTokens)}</span>
                <span>사용 {formatTokenCount(usedTokens)}</span>
              </div>
              {remainingTokens <= 0 && !tokenBalanceLoading && (
                <div style={{ marginTop: 10, color: '#dc2626', fontSize: 13 }}>
                  잔여 토큰이 없어 LLM 호출이 차단될 수 있습니다.
                </div>
              )}
            </div>

            <div className="sa-stat-card">
              <div className="sa-stat-label">토큰 관리</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="sa-btn sa-btn-primary sa-btn-sm"
                  onClick={() => setShowTopupModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> 충전하기
                </button>
                <button
                  type="button"
                  className="sa-btn sa-btn-secondary sa-btn-sm"
                  onClick={handleTokenReset}
                  disabled={resetSaving || tokenBalanceLoading}
                >
                  <FontAwesomeIcon icon={faRotateLeft} /> {resetSaving ? '초기화 중...' : '기록 초기화'}
                </button>
              </div>
              <div style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>
                충전은 한도 추가, 기록 초기화는 사용·지급 내역을 지우고 100,000 토큰으로 다시 시작합니다.
              </div>
              <button
                type="button"
                className="sa-btn-link"
                onClick={() => navigate(`/super-admin/companies/${companyId}/usage`)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12 }}
              >
                <FontAwesomeIcon icon={faChartLine} /> LLM 사용량 보기
              </button>
            </div>

            <div className="sa-stat-card">
              <div className="sa-stat-label">소속 계정</div>
              <div className="sa-stat-value">{formatTokenCount(members.length)}</div>
              <div style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>
                기업에 연결된 계정 수입니다.
              </div>
            </div>
          </div>

          <CompanyPersonaSchemaPanel companyId={companyId} disabled={isDeleted} />

          <div className="sa-table-container">
            <div className="sa-table-toolbar">
              <div className="sa-table-toolbar-left">
                <h3 style={{ margin: 0, color: '#111827', fontSize: 16 }}>소속 계정</h3>
              </div>
            </div>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>이메일</th>
                  <th>이름</th>
                  <th>소속 부서</th>
                  <th>Weighted Tokens</th>
                  <th>Raw Tokens</th>
                  <th>마지막 사용일시</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="sa-empty">
                        <div className="sa-empty-text">소속 계정이 없습니다</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  members.map((member) => {
                    const llmUsage = getMemberLlmUsage(member);
                    return (
                      <tr key={member.id || member.user_id}>
                        <td style={{ fontSize: 13, color: '#374151' }}>{member.email || '-'}</td>
                        <td style={{ fontWeight: 500, color: '#111827' }}>{member.name || '-'}</td>
                        <td>{member.department || '-'}</td>
                        <td style={{ fontWeight: 600 }}>{formatTokenCount(llmUsage.weightedTokens)}</td>
                        <td>{formatTokenCount(llmUsage.rawTokens)}</td>
                        <td style={{ fontSize: 13, color: '#6b7280' }}>{formatDate(llmUsage.lastUsedAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showTopupModal && (
        <div className="topup-modal-overlay" role="presentation">
          <form className="topup-modal" onSubmit={handleTopupSubmit} onClick={(event) => event.stopPropagation()}>
            <h3>토큰 충전</h3>
            <div className="topup-field">
              <label htmlFor="topup-tokens">Weighted tokens</label>
              <input
                id="topup-tokens"
                type="number"
                min="1"
                step="1"
                className="sa-search-input"
                value={topupTokens}
                onChange={(event) => setTopupTokens(event.target.value)}
                placeholder="100000"
                disabled={topupSaving}
                autoFocus
              />
            </div>
            <div className="topup-field">
              <label htmlFor="topup-note">메모</label>
              <textarea
                id="topup-note"
                className="sa-search-input"
                value={topupNote}
                onChange={(event) => setTopupNote(event.target.value)}
                placeholder="추가 구매분"
                disabled={topupSaving}
              />
            </div>
            {topupError && <div className="topup-error">{topupError}</div>}
            <div className="topup-actions">
              <button
                type="button"
                className="sa-btn sa-btn-secondary sa-btn-sm"
                onClick={() => setShowTopupModal(false)}
                disabled={topupSaving}
              >
                취소
              </button>
              <button type="submit" className="sa-btn sa-btn-primary sa-btn-sm" disabled={topupSaving}>
                {topupSaving ? '충전 중...' : '충전'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default CompanyDetailPage;
